/**
 * PostHog analytics client for tracking user events.
 * API key is loaded from environment variable POSTHOG_KEY.
 */

import posthog from "posthog-js";

// PostHog API key from environment
const POSTHOG_KEY =
  typeof window !== "undefined"
    ? (window as unknown as { ENV?: { POSTHOG_KEY?: string } }).ENV?.POSTHOG_KEY || ""
    : "";

// PostHog host — read from injected ENV, defaulting to US cloud (the project's
// region). Falls back to the literal only if ENV is absent.
const POSTHOG_HOST =
  (typeof window !== "undefined"
    ? (window as unknown as { ENV?: { POSTHOG_HOST?: string } }).ENV?.POSTHOG_HOST
    : "") || "https://us.i.posthog.com";

// PostHog group type modelling a merchant store/account. MUST match the value
// used server-side (core-api app/core/analytics.py, analytics.server.ts) so
// browser + backend events roll up to the same account in PostHog.
const STORE_GROUP = "store";

let initialized = false;

/**
 * Persist first-touch UTM params as super-properties so a store's marketing
 * source sticks to every event — enabling acquisition→install→paid attribution
 * (which campaign produced paying stores, not just signups). register_once means
 * the FIRST campaign that brought the visitor wins, even across later pageviews.
 */
function captureUtmFirstTouch(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  if (Object.keys(utm).length > 0) {
    posthog.register_once(utm);
  }
}

/**
 * Initialize PostHog analytics.
 * Should be called once on app load (client-side only).
 */
export function initAnalytics(): void {
  if (typeof window === "undefined" || initialized || !POSTHOG_KEY) {
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Capture pageviews + interaction autocapture so we get behavioural pattern
    // data (which screens, which buttons) without hand-instrumenting every click.
    capture_pageview: true,
    autocapture: true,
    // Respect Do Not Track
    respect_dnt: true,
    // Persist user identity across sessions
    persistence: "localStorage",
    // Only enable in production
    loaded: (posthog) => {
      if (process.env.NODE_ENV !== "production") {
        posthog.opt_out_capturing();
      }
      captureUtmFirstTouch();
    },
  });

  initialized = true;
}

/**
 * Identify the current store and bind it to its PostHog account group.
 *
 * `storeDomain` is the myshopify domain — the SAME identity key used by the
 * server-side capture in core-api and analytics.server.ts, so this browser's
 * events stitch onto the store's full cross-service timeline.
 *
 * Passing `plan` (and any other props) sets them on the person, registers them
 * as super-properties so every subsequent event is segmentable by plan without
 * re-passing it, and updates the `store` group so account-level funnels /
 * retention / revenue are keyed correctly.
 */
export function identifyUser(storeDomain: string, properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;

  posthog.identify(storeDomain, {
    store_domain: storeDomain,
    ...properties,
  });

  // Attach the store account group so this store's events aggregate per-account.
  posthog.group(STORE_GROUP, storeDomain, {
    name: storeDomain,
    ...properties,
  });

  // Register plan (and other stable context) as super-properties so every event
  // carries it — no need to thread `plan` through each track* call.
  if (properties && Object.keys(properties).length > 0) {
    posthog.register(properties);
  }
}

/**
 * Reset user identity (e.g., on logout/uninstall).
 */
export function resetIdentity(): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.reset();
}

// =============================================================================
// Event Tracking Functions
// =============================================================================

/**
 * Track app installation event.
 */
export function trackAppInstalled(properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("app_installed", properties);
}

/**
 * Track onboarding started event.
 */
export function trackOnboardingStarted(properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("onboarding_started", properties);
}

/**
 * Track onboarding completed event.
 */
export function trackOnboardingCompleted(properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("onboarding_completed", properties);
}

/**
 * Track collection created event.
 */
export function trackCollectionCreated(properties?: {
  collection_id?: string;
  collection_title?: string;
  product_count?: number;
  source?: "manual" | "opportunity";
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("collection_created", properties);
}

/**
 * Track opportunity viewed event.
 */
export function trackOpportunityViewed(properties?: {
  opportunity_id?: string;
  opportunity_type?: string;
  signal_type?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("opportunity_viewed", properties);
}

/**
 * Track opportunity accepted event.
 */
export function trackOpportunityAccepted(properties?: {
  opportunity_id?: string;
  opportunity_type?: string;
  signal_type?: string;
  collection_id?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("opportunity_accepted", properties);
}

/**
 * Track template used event.
 */
export function trackTemplateUsed(properties?: {
  template_id?: string;
  template_name?: string;
  category?: string;
  is_global?: boolean;
  collection_id?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("template_used", properties);
}

/**
 * Track upgrade modal viewed event.
 */
export function trackUpgradeModalViewed(properties?: {
  trigger?: string;
  current_plan?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_modal_viewed", properties);
}

/**
 * Track upgrade started event (user clicked upgrade button).
 */
export function trackUpgradeStarted(properties?: {
  target_plan?: string;
  current_plan?: string;
  trigger?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_started", properties);
}

/**
 * Track a completed paid conversion (Shopify charge approved & plan activated).
 * Fired when the pricing page loads after a successful billing callback, so the
 * install -> trial -> paid funnel is measurable end-to-end (previously only
 * upgrade_started was tracked, leaving conversions invisible).
 */
export function trackUpgradeCompleted(properties?: {
  plan?: string;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_completed", properties);
}

/**
 * Track page view event (for custom page tracking).
 */
export function trackPageView(pageName: string, properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("$pageview", {
    page_name: pageName,
    ...properties,
  });
}

/**
 * Generic event tracking function for custom events.
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture(eventName, properties);
}

// =============================================================================
// A/B Testing Event Tracking
// =============================================================================

/**
 * Track upgrade modal shown event (with message_key for A/B testing).
 */
export function trackUpgradeModalShown(properties: {
  message_key: string;
  trigger?: string;
  current_plan?: string;
  experiment_group?: string | null;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_modal_shown", properties);
}

/**
 * Track upgrade modal dismissed event (with message_key for A/B testing).
 */
export function trackUpgradeModalDismissed(properties: {
  message_key: string;
  trigger?: string;
  current_plan?: string;
  experiment_group?: string | null;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_modal_dismissed", properties);
}

/**
 * Track upgrade modal converted event (with message_key for A/B testing).
 */
export function trackUpgradeModalConverted(properties: {
  message_key: string;
  target_plan?: string;
  current_plan?: string;
  experiment_group?: string | null;
}): void {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  posthog.capture("upgrade_modal_converted", properties);
}
